/**
 * useCurrentUser Hook
 *
 * 현재 로그인한 사용자의 User 도메인 정보를 제공합니다.
 *
 * 책임:
 * - AuthUser(인증 정보)와 User(도메인 정보)를 email 기반으로 매핑
 * - 현재 사용자의 role 정보 제공 (owner/member)
 * - 권한 확인을 위한 isOwner, isMember 헬퍼 제공
 *
 * @fix BUG-009: 권한 관리 미구현 해결
 * @why 권한 검증을 위해 현재 로그인 유저의 role 정보가 필요하지만,
 *      useAuth()는 Google OAuth 인증 정보만 제공하므로
 *      AuthUser.email과 User.email을 매핑하여 도메인 정보를 가져옵니다.
 */

import { useState, useEffect } from 'react'
import { useAuth } from '@/core/auth/useAuth'
import { userRepository } from '../repositories'
import type { User } from '../types/User'

/**
 * useCurrentUser Return Type
 */
export interface UseCurrentUserReturn {
  /** 현재 로그인한 사용자 정보 (role 포함) */
  currentUser: User | null
  /** 로딩 상태 */
  loading: boolean
  /** 에러 상태 */
  error: Error | null
  /** Owner 여부 */
  isOwner: boolean
  /** Member 여부 */
  isMember: boolean
}

/**
 * 현재 로그인한 사용자 정보 Hook
 *
 * AuthUser.email을 사용하여 User 도메인 정보를 조회합니다.
 *
 * @returns 현재 사용자 정보 및 상태
 *
 * @example
 * ```tsx
 * function MembersPage() {
 *   const { currentUser, loading, isOwner } = useCurrentUser()
 *
 *   if (loading) return <div>로딩 중...</div>
 *   if (!currentUser) return <div>로그인이 필요합니다</div>
 *
 *   return (
 *     <div>
 *       <h1>{currentUser.name}님, 환영합니다</h1>
 *       {isOwner && <button>관리자 메뉴</button>}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // 권한 검증에 사용
 * const { currentUser } = useCurrentUser()
 *
 * const handleChangeRole = async (targetUser: User) => {
 *   if (!currentUser) {
 *     alert('현재 사용자 정보를 불러올 수 없습니다')
 *     return
 *   }
 *
 *   if (!canChangeUserRole(currentUser, targetUser, newRole)) {
 *     alert('권한이 없습니다')
 *     return
 *   }
 *
 *   await updateUserRole(targetUser.id, newRole)
 * }
 * ```
 */
export function useCurrentUser(): UseCurrentUserReturn {
  const { user: authUser, isAuthenticated } = useAuth()
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // 인증되지 않았거나 authUser가 없으면 초기화
    if (!isAuthenticated || !authUser) {
      setCurrentUser(null)
      setLoading(false)
      return
    }

    /**
     * 현재 로그인 유저의 User 도메인 정보 조회
     *
     * @why AuthUser.email과 User.email을 매핑하여
     *      현재 로그인 유저의 role 정보를 가져옵니다.
     */
    const fetchCurrentUser = async () => {
      try {
        setLoading(true)
        setError(null)

        // AuthUser.email로 User 도메인 정보 조회
        const user = await userRepository.findByEmail(authUser.email)

        if (!user) {
          throw new Error(
            '현재 사용자 정보를 찾을 수 없습니다. 워크스페이스에 등록되지 않은 사용자입니다.'
          )
        }

        setCurrentUser(user)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
        console.error('[useCurrentUser] 현재 사용자 정보 조회 실패:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCurrentUser()
  }, [authUser?.email, isAuthenticated])

  return {
    currentUser,
    loading,
    error,
    isOwner: currentUser?.role === 'owner',
    isMember: currentUser?.role === 'member',
  }
}
